import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, CircularProgress, Alert, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import PaymentIcon from '@mui/icons-material/Payment';

/**
 * 命理师预约纯服务器端支付按钮
 * 
 * 该组件完全不依赖PayPal前端SDK，而是通过Firebase云函数与PayPal后端API交互
 */
const FortuneServerPayButton = ({ bookingId, amount, onSuccess, onErrorCallback }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // 获取Firebase Functions实例 - 使用正确的应用实例引用
  const functions = getFunctions(getApp(), 'asia-southeast1');
  
  // 用于追踪是否正在处理支付回调的标志
  const isProcessingPayment = useRef(false);
  
  // 检查URL参数中是否包含PayPal返回的token
  useEffect(() => {
    const checkPaymentParams = async () => {
      // 防止重复处理
      if (isProcessingPayment.current) {
        console.log('支付回调已在处理中，跳过重复处理');
        return;
      }
      
      const urlParams = new URLSearchParams(location.search);
      const token = urlParams.get('token');
      const payerId = urlParams.get('PayerID');
      const localBookingId = urlParams.get('bookingId') || bookingId;
      const paymentSuccess = urlParams.get('success') === 'true';
      
      // 如果URL明确指示支付成功
      if (paymentSuccess && localBookingId) {
        console.log('URL参数指示支付成功，尝试验证支付状态');
        isProcessingPayment.current = true;
        
        try {
          setLoading(true);
          setError('');
          
          // 尝试从预约记录中获取
          const bookingDoc = await getDoc(doc(db, 'bookings', localBookingId));
          
          if (bookingDoc.exists()) {
            const bookingData = bookingDoc.data();
            
            // 如果支付已完成，直接报告成功
            if (bookingData.paymentStatus === 'completed' || bookingData.status === 'confirmed') {
              console.log('支付已完成，直接报告成功');
              
              if (onSuccess) {
                onSuccess({
                  success: true,
                  bookingId: localBookingId,
                  orderId: bookingData.paypalOrderId || 'unknown'
                });
              }
              
              return;
            }
            
            // 如果有PayPal订单ID，尝试再次捕获
            if (bookingData.paypalOrderId) {
              try {
                await handleCapturePayment(bookingData.paypalOrderId, localBookingId);
              } catch (captureError) {
                console.log('再次捕获失败，但可能已成功:', captureError);
                
                // 即使捕获失败，也假设支付成功 
                if (onSuccess) {
                  onSuccess({
                    success: true,
                    bookingId: localBookingId,
                    possiblyProcessed: true,
                    orderId: bookingData.paypalOrderId || 'unknown'
                  });
                }
              }
            }
          }
        } catch (err) {
          console.error('处理URL支付成功参数错误:', err);
        } finally {
          setLoading(false);
          isProcessingPayment.current = false;
          
          // 清除URL参数
          try {
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
          } catch (e) {}
        }
        
        return;
      }
      
      // 如果URL中包含token和PayerID，表示支付已被批准，需要捕获支付
      if (token && payerId && localBookingId) {
        console.log('检测到PayPal重定向回调，开始处理支付:', { token, payerId, localBookingId });
        
        // 设置标志，防止重复处理
        isProcessingPayment.current = true;
        
        try {
          setLoading(true);
          setError('');
          
          // 从localStorage获取orderId
          const paypalOrderId = localStorage.getItem(`booking_paypal_${localBookingId}`);
          
          if (!paypalOrderId) {
            console.log('未找到PayPal订单ID，尝试从预约记录中获取');
            // 尝试从预约记录中获取
            const bookingDoc = await getDoc(doc(db, 'bookings', localBookingId));
            if (bookingDoc.exists() && bookingDoc.data().paypalOrderId) {
              const orderId = bookingDoc.data().paypalOrderId;
              console.log('从预约记录获取到PayPal订单ID:', orderId);
              
              try {
                await handleCapturePayment(orderId, localBookingId);
              } catch (captureError) {
                // 检查是否为连接错误
                const isConnectionError = 
                  captureError.message?.includes('connection') || 
                  captureError.message?.includes('Receiving end') ||
                  captureError.message?.includes('Window closed');
                
                // 如果是连接错误，假设支付可能已成功
                if (isConnectionError) {
                  console.log('检测到连接错误，标记支付为待处理:', captureError.message);
                  
                  // 更新预约状态为待处理
                  await updateDoc(doc(db, 'bookings', localBookingId), {
                    paymentErrorType: 'connection_lost',
                    paymentPossiblyCompleted: true,
                    paymentErrorMessage: captureError.message,
                    paymentRetryPending: true,
                    updatedAt: serverTimestamp()
                  });
                  
                  // 触发成功回调，但标记为可能已处理
                  if (onSuccess) {
                    onSuccess({
                      success: true,
                      bookingId: localBookingId,
                      orderId: orderId,
                      possiblyProcessed: true
                    });
                  }
                  
                  return;
                } else {
                  // 其他错误正常抛出
                  throw captureError;
                }
              }
            } else {
              throw new Error('未找到PayPal订单信息，无法完成支付');
            }
          } else {
            console.log('从本地存储获取到PayPal订单ID:', paypalOrderId);
            
            try {
              await handleCapturePayment(paypalOrderId, localBookingId);
            } catch (captureError) {
              // 检查是否为连接错误
              const isConnectionError = 
                captureError.message?.includes('connection') || 
                captureError.message?.includes('Receiving end') ||
                captureError.message?.includes('Window closed');
              
              // 如果是连接错误，假设支付可能已成功
              if (isConnectionError) {
                console.log('检测到连接错误，标记支付为待处理:', captureError.message);
                
                // 更新预约状态为待处理
                await updateDoc(doc(db, 'bookings', localBookingId), {
                  paymentErrorType: 'connection_lost',
                  paymentPossiblyCompleted: true,
                  paymentErrorMessage: captureError.message,
                  paymentRetryPending: true,
                  updatedAt: serverTimestamp()
                });
                
                // 触发成功回调，但标记为可能已处理
                if (onSuccess) {
                  onSuccess({
                    success: true,
                    bookingId: localBookingId,
                    orderId: paypalOrderId,
                    possiblyProcessed: true
                  });
                }
                
                return;
              } else {
                // 其他错误正常抛出
                throw captureError;
              }
            }
          }
        } catch (err) {
          console.error('处理PayPal回调错误:', err);
          setError(`支付处理失败: ${err.message || '未知错误'}`);
          
          if (onErrorCallback) {
            onErrorCallback(err);
          }
          
          // 重置标志，允许用户重试
          isProcessingPayment.current = false;
        } finally {
          setLoading(false);
          
          // 清除URL中的参数，避免刷新页面时重复处理
          try {
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
            console.log('已清除URL参数，避免重复处理');
          } catch (e) {
            console.warn('清除URL参数失败:', e);
          }
        }
      }
    };
    
    checkPaymentParams();
  }, [location.search, bookingId]);
  
  // 防止重复支付的标志
  const isCreatingPayment = useRef(false);
  
  // 创建支付
  const handleCreatePayment = async () => {
    // 防止重复点击
    if (isCreatingPayment.current) {
      console.log('支付请求正在处理中，请勿重复点击');
      return;
    }
    
    if (!currentUser) {
      console.log('用户未登录，跳转到登录页面');
      navigate('/login', { state: { from: '/fortune-teller' } });
      return;
    }
    
    try {
      isCreatingPayment.current = true;
      setLoading(true);
      setError('');
      
      // 添加更多日志用于排查问题
      console.log(`开始处理预约支付 - bookingId: ${bookingId}, amount: ${amount}, user: ${currentUser.uid}`);
      
      // 尝试从Firestore加载预约信息以验证
      try {
        const bookingDoc = await getDoc(doc(db, 'bookings', bookingId));
        if (bookingDoc.exists()) {
          console.log('预约信息验证成功:', bookingDoc.id);
        } else {
          console.warn('找不到预约信息:', bookingId);
        }
      } catch (verifyError) {
        console.error('验证预约信息失败:', verifyError);
        // 继续处理，不中断流程
      }
      
      // 使用capturePaypalOrderV3函数创建订单，这是一个更强大的函数
      console.log('使用capturePaypalOrderV3云函数创建订单...');
      
      // 声明在外部作用域，使其可以在try/catch块外使用
      let finalResult;
      
      try {
        const createOrderFunc = httpsCallable(functions, 'serverPaypal-createOrder');
        console.log('成功获取serverPaypal-createOrder函数');
        
        // 构建返回URL，包含必要的bookingId参数，并添加时间戳防止缓存
        const timestamp = new Date().getTime();
        const returnUrl = `${window.location.origin}${window.location.pathname}?bookingId=${bookingId}&t=${timestamp}`;
        const cancelUrl = `${window.location.origin}${window.location.pathname}?cancelled=true&bookingId=${bookingId}&t=${timestamp}`;
        
        // 调用云函数
        const result = await createOrderFunc({
          paymentType: 'fortune-booking',
          bookingId: bookingId,
          amount: parseFloat(amount),
          currency: 'SGD',
          returnUrl: returnUrl,
          cancelUrl: cancelUrl
        });
        
        console.log('云函数返回结果:', result.data);
        // 立即保存结果，不等到try/catch块结束
        finalResult = result.data;
      } catch (fnError) {
        console.error('serverPaypal-createOrder函数调用失败:', fnError);
        
        // 降级到标准createPaypalOrder函数
        console.log('降级到标准createPaypalOrder函数...');
        const createOrderFunction = httpsCallable(functions, 'createPaypalOrder');
        
        // 构建返回URL，包含必要的bookingId参数，并添加时间戳防止缓存
        const timestamp = new Date().getTime();
        const returnUrl = `${window.location.origin}${window.location.pathname}?bookingId=${bookingId}&t=${timestamp}`;
        const cancelUrl = `${window.location.origin}${window.location.pathname}?cancelled=true&bookingId=${bookingId}&t=${timestamp}`;
        
        try {
          // 尝试使用标准函数 - 嵌套try块以捕获此处可能发生的错误
          const result = await createOrderFunction({
            paymentType: 'fortune-booking',
            bookingId: bookingId,
            amount: parseFloat(amount),
            currency: 'SGD',
            returnUrl: returnUrl,
            cancelUrl: cancelUrl
          });
          
          console.log('标准云函数返回结果:', result.data);
          // 保存标准函数的结果
          finalResult = result.data;
        } catch (standardError) {
          console.error('标准createPaypalOrder函数调用也失败:', standardError);
          throw standardError; // 重新抛出错误让外部catch处理
        }
      }
      
      // 运行到这里意味着要么serverPaypal-createOrder成功，要么fallback到createPaypalOrder成功
      // 现在处理结果
      
      // 适配不同返回格式
      let orderId, approveUrl;
      
      try {
        // 检查finalResult是否有效
        if (!finalResult) {
          throw new Error('No result data');
        }
        
        // 如果返回标准success字段
        if (finalResult.success === true) {
          // 从success结果提取字段
          orderId = finalResult.orderId || finalResult.id;
          
          // 尝试获取approveUrl
          if (finalResult.approveUrl) {
            approveUrl = finalResult.approveUrl;
          } else if (finalResult.links && Array.isArray(finalResult.links)) {
            // 从links数组中查找approve链接
            const approveLink = finalResult.links.find(link => 
              link.rel === 'approve' || link.rel === 'approval_url');
            if (approveLink && approveLink.href) {
              approveUrl = approveLink.href;
            }
          }
        } else {
          // 直接从结果中提取字段
          orderId = finalResult.id || finalResult.orderId;
          
          // 尝试获取approveUrl
          if (finalResult.links && Array.isArray(finalResult.links)) {
            const approveLink = finalResult.links.find(link => 
              link.rel === 'approve' || link.rel === 'approval_url');
            if (approveLink && approveLink.href) {
              approveUrl = approveLink.href;
            }
          }
        }
        
        // 尝试其它已知结构
        if (!orderId && finalResult.order_id) {
          orderId = finalResult.order_id;
        }
        
        if (!approveUrl && finalResult.approval_url) {
          approveUrl = finalResult.approval_url;
        }
      } catch (parseError) {
        console.error('解析结果数据失败:', parseError);
        console.log('原始结果数据:', result?.data);
        throw new Error('解析支付结果失败');
      }
      
      console.log('提取的订单ID:', orderId);
      console.log('提取的支付链接:', approveUrl);
      
      if (!orderId || !approveUrl) {
        console.error('无法从响应中提取必要信息:', finalResult);
        throw new Error('创建订单成功但未返回必要信息');
      }
      
      // 更新预约状态
      try {
        console.log(`更新预约状态 - ID: ${bookingId}, PayPal订单ID: ${orderId}`);
        const bookingRef = doc(db, 'bookings', bookingId);
        await updateDoc(bookingRef, {
          paymentProcessing: true,
          paymentStartTime: serverTimestamp(),
          paymentMethod: 'paypal_server',
          paypalOrderId: orderId,
          updatedAt: serverTimestamp()
        });
        console.log('预约状态更新成功');
      } catch (updateError) {
        console.error('更新预约状态失败:', updateError);
        // 继续处理，不阻断支付流程
      }
      
      // 存储订单ID到localStorage，以便支付成功后使用
      localStorage.setItem(`booking_paypal_${bookingId}`, orderId);
      console.log(`订单ID已存储到localStorage - key: booking_paypal_${bookingId}, value: ${orderId}`);
      
      // 显示调试信息
      console.log('支付流程完成，准备重定向到PayPal支付页面:', approveUrl);
      
      // 设置一个短暂的延迟，以确保UI状态更新
      setTimeout(() => {
        console.log('正在重定向到PayPal...');
        // 重定向到PayPal支付页面
        window.location.href = approveUrl;
      }, 300);
    } catch (err) {
      console.error('创建支付失败:', err);
      setError(`支付错误: ${err.message || '创建订单失败'}`);
      
      if (onErrorCallback) {
        onErrorCallback(err);
      }
      // 重置标志，允许用户重试
      isCreatingPayment.current = false;
    } finally {
      setLoading(false);
    }
  };
  
  // 捕获支付
  const handleCapturePayment = async (orderId, localBookingId) => {
    try {
      console.log('开始捕获支付订单:', orderId);
      
      // 准备请求数据
      const requestData = {
        orderId: orderId,
        bookingId: localBookingId,
        paymentType: 'fortune-booking',
        source: 'FortuneServerPayButton',
        timestamp: Date.now()
      };
      
      let result;
      
      // 先尝试使用增强版函数，如果失败则降级到标准版
      try {
        // 使用增强版V3函数，具有更好的错误处理和自动创建订单功能
        console.log('尝试使用增强版capturePaypalOrderV3云函数...');
        const enhancedCaptureFunc = httpsCallable(functions, 'capturePaypalOrderV3');
        result = await enhancedCaptureFunc(requestData);
        console.log('增强版捕获成功:', result.data);
      } catch (enhancedError) {
        console.log('增强版函数调用失败，降级到标准capturePaypalOrder函数:', enhancedError.message);
        try {
          // 降级到标准函数
          console.log('使用标准capturePaypalOrder云函数...');
          const standardCaptureFunc = httpsCallable(functions, 'capturePaypalOrder');
          result = await standardCaptureFunc(requestData);
          console.log('标准捕获成功:', result.data);
        } catch (standardError) {
          console.error('标准捕获函数调用也失败:', standardError.message);
          
          // 检查错误是否为连接中断错误
          const isConnectionError = 
            standardError.message?.includes('connection') || 
            standardError.message?.includes('Receiving end') ||
            standardError.message?.includes('Window closed') || 
            standardError.message?.includes('closed window');
          
          if (isConnectionError) {
            console.log('检测到连接中断错误，尝试通过备用方式验证支付状态');
            
            // 假设支付可能已成功，尝试通过更新预约状态来处理
            try {
              // 更新预约状态为处理中
              const bookingRef = doc(db, 'bookings', localBookingId);
              await updateDoc(bookingRef, {
                paymentErrorType: 'connection_lost',
                paymentErrorMessage: standardError.message,
                paymentRetryPending: true,
                updatedAt: serverTimestamp()
              });
              
              // 返回一个"可能已处理"的结果
              return {
                success: true,
                status: 'PROCESSING',
                possiblyProcessed: true,
                message: '支付可能已完成，但连接中断。系统将自动验证支付状态。'
              };
            } catch (updateError) {
              console.error('更新预约状态失败:', updateError);
              throw new Error('支付状态未知，请刷新页面或联系客服');
            }
          } else {
            throw standardError; // 重新抛出错误以便外层catch处理
          }
        }
      }
      
      console.log('捕获订单结果:', result.data);
      console.log('检查捕获返回结果结构:', JSON.stringify(result.data, null, 2));
      
      // 设置一个成功标志
      let captureSuccess = false;
      
      // 检查不同格式的返回结果
      if (result.data && result.data.success === true) {
        // 标准success格式
        captureSuccess = true;
      } else if (result.data && result.data.status) {
        // 检查状态值
        captureSuccess = ['COMPLETED', 'APPROVED', 'CAPTURED'].includes(result.data.status);
      } else if (result.data && result.data.captureDetails) {
        // 检查captureDetails状态
        captureSuccess = result.data.captureDetails && 
                        ['COMPLETED', 'APPROVED', 'CAPTURED'].includes(result.data.captureDetails.status);
      }
      
      if (!captureSuccess) {
        console.error('捕获支付失败:', result.data);
        throw new Error('捕获支付失败: ' + (result.data?.message || '未知错误'));
      }
      
      // 更新预约状态
      try {
        const bookingRef = doc(db, 'bookings', localBookingId);
        await updateDoc(bookingRef, {
          status: 'confirmed',
          paymentStatus: 'completed',
          paymentCaptureId: orderId,
          paymentTime: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        console.log('预约支付状态已更新为已完成');
      } catch (updateError) {
        // 即使更新预约状态失败，仍然继续处理并将支付视为成功
        // 这样用户界面不会受到影响，我们将错误记录起来
        console.warn('更新预约记录失败，但支付已成功完成:', updateError);
        console.log('由于捕获成功，将继续处理支付成功流程');
        
        // 记录支付可能成功但数据库未更新的情况
        try {
          // 存储支付信息到localStorage，以便后续恢复
          localStorage.setItem(`booking_payment_${localBookingId}`, JSON.stringify({
            orderId: orderId,
            status: 'completed',
            timestamp: Date.now(),
            updateFailed: true
          }));
        } catch (e) {
          console.error('存储恢复数据失败:', e);
        }
      }
      
      // 清除localStorage中的订单ID
      localStorage.removeItem(`booking_paypal_${localBookingId}`);
      
      // 调用成功回调 - 无论数据库更新是否成功，都返回成功结果
      if (onSuccess) {
        onSuccess({
          success: true,
          bookingId: localBookingId,
          orderId: orderId
        });
      }
      
      return true;
    } catch (err) {
      console.error('捕获支付失败:', err);
      
      // 更新预约状态为支付错误
      try {
        const bookingRef = doc(db, 'bookings', localBookingId);
        await updateDoc(bookingRef, {
          paymentErrorTime: serverTimestamp(),
          paymentErrorMessage: err.message || '捕获支付失败',
          updatedAt: serverTimestamp()
        });
      } catch (updateError) {
        console.error('更新预约错误状态失败:', updateError);
        
        // 记录错误信息到localStorage
        try {
          localStorage.setItem(`booking_payment_error_${localBookingId}`, JSON.stringify({
            error: err.message || '捕获支付失败',
            timestamp: Date.now(),
            orderId: orderId
          }));
        } catch (e) {
          console.error('存储错误信息失败:', e);
        }
      }
      
      // 检查错误类型 - 如果是权限错误但支付已成功，我们可以尝试恢复
      if (err.code === 'permission-denied' || err.message?.includes('Missing or insufficient permissions')) {
        console.warn('检测到权限错误，但支付可能已成功完成。尝试恢复流程...');
        
        // 在localStorage中标记此次预约支付可能已成功但需要管理员检查
        try {
          localStorage.setItem(`booking_admin_check_${localBookingId}`, JSON.stringify({
            orderId: orderId,
            timestamp: Date.now(),
            paymentPossiblyCompleted: true,
            errorMessage: err.message
          }));
          
          // 为了用户体验，我们可以调用成功回调，但标记为需要验证
          if (onSuccess) {
            onSuccess({
              success: true,
              bookingId: localBookingId,
              orderId: orderId,
              requiresVerification: true,
              possiblyProcessed: true
            });
            // 由于已经调用了成功回调，我们不再抛出错误
            return;
          }
        } catch (e) {
          console.error('存储管理员检查数据失败:', e);
        }
      }
      
      throw err;
    }
  };
  
  // 检查URL参数是否包含取消信息
  useEffect(() => {
    const checkCancellation = async () => {
      const urlParams = new URLSearchParams(location.search);
      const cancelled = urlParams.get('cancelled');
      const localBookingId = urlParams.get('bookingId') || bookingId;
      
      if (cancelled === 'true' && localBookingId) {
        console.log('检测到支付取消');
        
        try {
          // 更新预约状态为已取消支付
          const bookingRef = doc(db, 'bookings', localBookingId);
          await updateDoc(bookingRef, {
            paymentProcessing: false,
            paymentCancelTime: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
          
          console.log('预约状态已更新为取消支付');
          
          // 清除localStorage中的订单ID
          localStorage.removeItem(`booking_paypal_${localBookingId}`);
          
          // 清除URL中的参数
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
        } catch (err) {
          console.error('更新预约取消状态失败:', err);
        }
      }
    };
    
    checkCancellation();
  }, [location.search, bookingId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
          正在处理支付请求...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        sx={{ mb: 2 }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => setError('')}
          >
            重试
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%', minHeight: 50 }}>
      {/* 确保有一个ID为paypal-server-button-container的容器 */}
      <div id="paypal-server-button-container" style={{ width: '100%' }}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          onClick={handleCreatePayment}
          startIcon={<PaymentIcon />}
          sx={{ 
            py: 1.5, 
            bgcolor: '#0070ba', 
            '&:hover': { bgcolor: '#005ea6' }
          }}
        >
          使用PayPal支付 ${parseFloat(amount).toFixed(2)}
        </Button>
      </div>
      
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
        点击按钮将打开PayPal支付页面，完成支付后将自动返回
      </Typography>
    </Box>
  );
};

export default FortuneServerPayButton;